import { SelectProps, Spin } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { Column, Flex } from '@ydistri/ds';
import { CreateAsoButton } from './CreateAsoButton';
import { SearchableSelectOptionType, selectFilterHandler } from '@ydistri/utils';
import { AsoRequestResponse } from '@ydistri/api-sdk';
import { useGetAsoRequestsQuery } from '../apiAso';
import {
  BackgroundVariant,
  dropdownAlign,
  OptionLeft,
  OptionRow,
  OptionTitle,
  StyledSelect,
} from './selectStyledComponents';
import { COLORS, computeRemSize } from '@ydistri/theme';

const requestsSorter = (left: AsoRequestResponse, right: AsoRequestResponse) => {
  return left.id - right.id;
};

const request2Option = (req: AsoRequestResponse) => ({
  value: req.id,
  label: (
    <OptionRow>
      <OptionLeft>#{req.id}</OptionLeft>
      <OptionTitle title={req.name}>{req.name}</OptionTitle>
    </OptionRow>
  ),
  searchableString: req.name,
});

export interface AsoRequestSelectProps {
  requestId?: number | null;
  createButtonAction?: () => void;
  backgroundVariant?: BackgroundVariant;
  onChange: (value: number) => void;
  loading?: boolean;
  requestIdsToRemove?: number[];
  placeholder?: React.ReactNode;
}

const AsoRequestSelect: React.FC<AsoRequestSelectProps> = ({
  onChange,
  createButtonAction,
  backgroundVariant = 'default',
  requestId,
  loading = false,
  requestIdsToRemove,
  placeholder,
}) => {
  const { data: requests, isFetching } = useGetAsoRequestsQuery();

  const options: SearchableSelectOptionType[] = useMemo(() => {
    if (requests) {
      return requests
        .filter(r => !requestIdsToRemove?.includes(r.id))
        .sort(requestsSorter)
        .map(request2Option);
    }
    return [];
  }, [requests, requestIdsToRemove]);

  const selectStyle = useMemo(() => ({ margin: '0.5rem' }), []);

  const dropdownStyle = useMemo(
    () => ({
      popup: {
        root: {
          borderLeft: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderRight: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderBottom: `${computeRemSize(1)} solid ${COLORS.BORDER}`,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          background:
            backgroundVariant === 'transparent-background' ? COLORS.PAGE_BACKGROUND : 'white',
          boxShadow: 'none',
        },
      },
    }),
    [backgroundVariant],
  );

  const dropdownRender: SelectProps['dropdownRender'] = useCallback(
    (origin: React.ReactElement) => {
      return (
        <Column $gap="0.5rem">
          {origin}
          {createButtonAction && (
            <Flex $padding={1}>
              <CreateAsoButton title="Create ASO request" onClick={createButtonAction} />
            </Flex>
          )}
        </Column>
      );
    },
    [createButtonAction],
  );

  return (
    <Spin spinning={isFetching}>
      <StyledSelect
        $backgroundVariant={backgroundVariant}
        value={requestId ?? undefined}
        onChange={onChange}
        options={options}
        showSearch={true}
        variant="outlined"
        size="large"
        style={selectStyle}
        filterOption={selectFilterHandler}
        dropdownAlign={dropdownAlign}
        styles={dropdownStyle}
        popupRender={dropdownRender}
        listHeight={window.innerHeight * 0.6}
        data-testid="aso-request-select"
        loading={loading}
        disabled={loading}
        placeholder={placeholder}
      />
    </Spin>
  );
};

export default AsoRequestSelect;
